/*
  Ian - 2026-09-03 - Scroll-triggered video autoplay and lazy loading.

  Two jobs, both measured on the live site before this was written.

  1) MAKE VIDEOS ACTUALLY AUTOPLAY on the homepage and on product pages.
     Envy parks every video inside a <template> in `snippets/deferred-media.liquid`
     and only injects it when something calls loadMedia(). That trigger
     (`slider:media:ready`) fires once, at swiper afterInit, and only reaches a
     deferred-media sitting in `.swiper-slide-active`. So a PDP video that is not the
     first media never loaded, the desktop grid layout never loaded at all, and the
     homepage section injected itself at page load with preload="none" and then
     stalled at readyState 0 showing its poster.

  2) STOP VIDEO DOWNLOADING BEFORE ANYONE HAS SEEN IT.
     The Alpine collection was pulling 14.6MB of 1080p video on page load, mobile
     included.

  ---------------------------------------------------------------------------
  Revised 3 Sep 2026 after Ian tested on a phone: it worked, but clunkily. The play
  button showed, vanished, and the video often did not start until he scrolled a bit
  further. Three causes, all fixed here and in `ian-custom.css`:

  a) The play button was visible until loadMedia() hid it, so it flashed on every
     video. It is now hidden in CSS from the moment this script runs, and put back
     only if playback is actually refused.

  b) Envy sets `deferred-media video{background-color:#000}` and the injected video
     shares a grid cell with the poster, painting over it. So the moment the video
     was injected you got a black box until the first frame arrived. The video is now
     held at opacity 0 until it genuinely starts playing, so the poster stays visible
     underneath and it crossfades.

  c) A single trigger both loaded AND played, firing 200px before the video was on
     screen. iOS will not start playback for an element that is not visible, so that
     play() was quietly refused and nothing retried it until the next scroll produced
     another callback. That is exactly the "scroll a bit more" symptom. Loading and
     playing are now separate: buffering starts 800px out so the file has time to
     arrive, playback is only attempted once the video is really on screen, and
     `canplay` retries it if the bytes land after that point.
  ---------------------------------------------------------------------------

  Videos are muted and playsinline, which is what iOS requires for inline autoplay.
  There is deliberately no connection-type check: the Network Information API does not
  exist in Safari, so it would do nothing on the device that matters. saveData and
  prefers-reduced-motion are honoured where present.

  Loaded by `snippets/deferred-media.liquid` and `snippets/ian-collection-promo-card.liquid`.
  Safe to include more than once.
*/

(function () {
  'use strict';

  if (window.__ianVideoAutoplay) return;
  window.__ianVideoAutoplay = true;

  // Tells the stylesheet this script is alive. Without it the CSS leaves the play
  // button alone, so a JS failure degrades to the theme's normal click-to-play
  // rather than to a poster with no way to start it.
  document.documentElement.classList.add('ian-video-js');

  // Far enough out that a heavy file has time to buffer before it is looked at.
  var PRELOAD_MARGIN = '800px 0px';
  // Playback is only attempted when the video is genuinely on screen: iOS refuses
  // otherwise, and a refused play() is silent.
  var PLAY_MARGIN = '0px 0px';
  var PLAY_THRESHOLD = 0.2;
  var SELECTOR = 'video[data-ian-lazy-video], deferred-media[data-auto-play="true"]';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Progressive enhancement only. Chrome exposes this, Safari does not.
  function dataSaverOn() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData === true) return true;
    return c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
  }

  var autoplayAllowed = !reducedMotion() && !dataSaverOn();

  function videoOf(el) {
    return el.tagName === 'VIDEO' ? el : el.querySelector('video');
  }

  function hostOf(el) {
    return el.tagName === 'VIDEO' ? (el.closest('deferred-media') || el) : el;
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  function blocked(host) {
    // Puts the play button back and drops the opacity hold, so the shopper always has
    // a way to start a video the browser would not start itself.
    if (host && host.setAttribute) host.setAttribute('data-ian-blocked', '');
  }

  // ---- stage 1: get the bytes moving, without trying to play yet ----

  function raisePreload(video) {
    if (!video) return;
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    if (video.preload !== 'auto') video.preload = 'auto';

    // Raising `preload` is only a hint and some browsers ignore it on an element that
    // has already settled at preload="none". One load() nudges it. Guarded, because
    // load() aborts whatever is in flight, so calling it repeatedly stalls the fetch
    // permanently.
    if (video.__ianLoadKicked) return;
    window.setTimeout(function () {
      if (video.__ianLoadKicked) return;
      if (video.networkState === 1 && video.readyState === 0) {
        video.__ianLoadKicked = true;
        try { video.load(); } catch (e) {}
      }
    }, 300);
  }

  // component-deferred-media.js injects the video behind a 10ms promise, so it is not
  // there on the click's own tick.
  function whenVideoAppears(host, triesLeft, callback) {
    var video = host.querySelector('video');
    if (video) return callback(video);
    if (triesLeft <= 0) return;
    window.setTimeout(function () {
      whenVideoAppears(host, triesLeft - 1, callback);
    }, 50);
  }

  function wire(host, video) {
    if (video.__ianWired) return;
    video.__ianWired = true;

    video.addEventListener('playing', function () {
      host.setAttribute('data-ian-playing', '');
      host.removeAttribute('data-ian-blocked');
    });

    // If the file finishes buffering after the video is already on screen, the
    // intersection callback has been and gone. This is the retry.
    video.addEventListener('canplay', function () {
      if (video.paused && onScreen(host)) attemptPlay(host);
    });
  }

  function prepare(el) {
    if (!autoplayAllowed) return;

    if (el.tagName === 'VIDEO') {
      raisePreload(el);
      return;
    }

    if (!el.getAttribute('data-media-loaded')) {
      // Going through the play button rather than calling loadMedia() directly keeps
      // the theme's own bookkeeping intact: it drops the template and pauses whatever
      // else is playing.
      var button = el.querySelector('[data-media-play-button]');
      if (button) button.click();
    }

    whenVideoAppears(el, 20, function (video) {
      wire(el, video);
      raisePreload(video);
    });
  }

  // ---- stage 2: play, once it is really on screen ----

  function attemptPlay(el) {
    if (!autoplayAllowed) return;

    var host = hostOf(el);
    var video = videoOf(el);

    if (!video) {
      // Not injected yet. Prepare it; `canplay` will pick playback up from there.
      prepare(el);
      return;
    }

    wire(host, video);
    raisePreload(video);

    var played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(function () {
        // Refused (Low Power Mode, a policy we cannot see). Hand the shopper the
        // play button back rather than leaving a dead poster.
        if (video.paused) blocked(host);
      });
    }
  }

  function pause(el) {
    var video = videoOf(el);
    if (video && !video.paused) video.pause();
  }

  if (!('IntersectionObserver' in window)) {
    // No observer: nothing can be scroll-triggered, so give the play button back and
    // leave the theme's click-to-play behaviour alone.
    document.querySelectorAll('deferred-media[data-auto-play="true"]').forEach(blocked);
    return;
  }

  var preloadObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      prepare(entry.target);
      preloadObserver.unobserve(entry.target);
    });
  }, { rootMargin: PRELOAD_MARGIN, threshold: 0 });

  var playObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) attemptPlay(entry.target);
      else pause(entry.target);
    });
  }, { rootMargin: PLAY_MARGIN, threshold: PLAY_THRESHOLD });

  var seen = new WeakSet();

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(function (el) {
      if (seen.has(el)) return;
      seen.add(el);
      if (!autoplayAllowed) { blocked(hostOf(el)); return; }
      preloadObserver.observe(el);
      playObserver.observe(el);
    });
  }

  function init() {
    scan(document);

    // Collection grids repaint on filter and pagination, and the theme editor
    // re-renders sections, so pick up anything added later.
    if ('MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          scan(document);
        }, 200);
      }).observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target || document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
